import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../../UI/Button';

// Product-specific results content
const resultsContent = {
  nad: {
    person1: "Sarah, 52",
    person2: "Michael, 58",
    benefits1: [
      "Increased cellular energy",
      "Enhanced mental clarity"
    ],
    benefits2: [
      "Improved athletic performance",
      "Natural anti-aging support"
    ]
  },
  glutathione: {
    person1: "Sarah, 52",
    person2: "Michael, 58",
    benefits1: [
      "Reduced fine lines & wrinkles",
      "Enhanced skin tone & texture"
    ],
    benefits2: [
      "Improved skin elasticity",
      "Natural cellular rejuvenation"
    ]
  },
  vitaminb12: {
    person1: "Sarah, 52",
    person2: "Michael, 58",
    benefits1: [
      "Sustained daily energy",
      "Improved mental focus"
    ],
    benefits2: [
      "Better mood & motivation",
      "Enhanced nervous system health"
    ]
  },
  lcarnitine: {
    person1: "Sarah, 52",
    person2: "Michael, 58",
    benefits1: [
      "Improved fat metabolism",
      "Enhanced body composition"
    ],
    benefits2: [
      "Increased athletic performance",
      "Better muscle definition"
    ]
  }
};

export default function ResultsScreen({ product, onContinue, onBack }) {
  const content = resultsContent[product?.id] || resultsContent.nad;

  // Auto-rotation state for mobile gallery (0 = person1, 1 = person2)
  const [activePersonIndex, setActivePersonIndex] = useState(0);

  // Auto-rotate every 5 seconds on mobile
  useEffect(() => {
    const timer = setInterval(() => {
      setActivePersonIndex((prev) => (prev === 0 ? 1 : 0));
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="max-w-2xl mx-auto"
    >
      <div className="bg-white rounded-luxury shadow-luxury p-8">
        {/* Title */}
        <h2 className="text-3xl md:text-4xl font-bold text-charcoal text-center mb-8">
          Visible Results in <span className="text-renuvia-gold whitespace-nowrap">8-12 Weeks</span>
        </h2>

        {/* Before/After Images */}
        {/* Mobile: Single carousel with auto-rotation */}
        <div className="md:hidden mb-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={activePersonIndex}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.5 }}
            >
              <h3 className="text-center font-semibold text-charcoal mb-3">
                {activePersonIndex === 0 ? content.person1 : content.person2}
              </h3>
              <div className="relative aspect-square bg-gradient-to-br from-warm-beige to-gray-100 rounded-xl overflow-hidden mb-4">
                <img
                  src={activePersonIndex === 0 ? "/images/boosters/woman-before-after.jpg" : "/images/boosters/man-before-after.jpg"}
                  alt={activePersonIndex === 0 ? "Woman before and after results" : "Man before and after results"}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute bottom-0 left-0 right-0 flex">
                  <div className="w-1/2 text-center py-2 bg-black bg-opacity-50 text-white text-xs font-semibold">
                    Before
                  </div>
                  <div className="w-1/2 text-center py-2 bg-renuvia-gold bg-opacity-90 text-white text-xs font-semibold">
                    After 12 weeks
                  </div>
                </div>
              </div>
              {/* Carousel dots */}
              <div className="flex justify-center gap-2">
                <button
                  onClick={() => setActivePersonIndex(0)}
                  className={`w-2 h-2 rounded-full transition-colors ${activePersonIndex === 0 ? 'bg-renuvia-gold' : 'bg-gray-300'}`}
                  aria-label="Show person 1"
                />
                <button
                  onClick={() => setActivePersonIndex(1)}
                  className={`w-2 h-2 rounded-full transition-colors ${activePersonIndex === 1 ? 'bg-renuvia-gold' : 'bg-gray-300'}`}
                  aria-label="Show person 2"
                />
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Desktop: Show both side-by-side */}
        <div className="hidden md:grid grid-cols-2 gap-6 mb-8">
          {/* Person 1 */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            <h3 className="text-center font-semibold text-charcoal mb-3">{content.person1}</h3>
            <div className="relative aspect-square bg-gradient-to-br from-warm-beige to-gray-100 rounded-xl overflow-hidden mb-4">
              <img
                src="/images/boosters/woman-before-after.jpg"
                alt="Woman before and after results"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute bottom-0 left-0 right-0 flex">
                <div className="w-1/2 text-center py-2 bg-black bg-opacity-50 text-white text-xs font-semibold">
                  Before
                </div>
                <div className="w-1/2 text-center py-2 bg-renuvia-gold bg-opacity-90 text-white text-xs font-semibold">
                  After 12 weeks
                </div>
              </div>
            </div>
          </motion.div>

          {/* Person 2 */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4 }}
          >
            <h3 className="text-center font-semibold text-charcoal mb-3">{content.person2}</h3>
            <div className="relative aspect-square bg-gradient-to-br from-warm-beige to-gray-100 rounded-xl overflow-hidden mb-4">
              <img
                src="/images/boosters/man-before-after.jpg"
                alt="Man before and after results"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute bottom-0 left-0 right-0 flex">
                <div className="w-1/2 text-center py-2 bg-black bg-opacity-50 text-white text-xs font-semibold">
                  Before
                </div>
                <div className="w-1/2 text-center py-2 bg-renuvia-gold bg-opacity-90 text-white text-xs font-semibold">
                  After 12 weeks
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Benefits Grid */}
        <div className="bg-warm-beige rounded-xl p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Left column benefits */}
            <div className="space-y-3">
              {content.benefits1.map((benefit, index) => (
                <motion.div
                  key={benefit}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 + index * 0.1 }}
                  className="flex items-center gap-2"
                >
                  <svg className="w-5 h-5 text-renuvia-gold flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-charcoal">{benefit}</span>
                </motion.div>
              ))}
            </div>

            {/* Right column benefits */}
            <div className="space-y-3">
              {content.benefits2.map((benefit, index) => (
                <motion.div
                  key={benefit}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8 + index * 0.1 }}
                  className="flex items-center gap-2"
                >
                  <svg className="w-5 h-5 text-renuvia-gold flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-charcoal">{benefit}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-4">
          <Button onClick={onBack} variant="ghost" className="w-1/4">
            ←
          </Button>
          <Button onClick={onContinue} variant="primary" className="w-3/4">
            Continue to Final Step →
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
